import { useAtomValue } from "jotai";
import Image from "next/image";
import { useRouter } from "next/router";
import { useMemo } from "react";

import { H4 } from "@sorcery/ui";

import { fullCollectionlistJotai } from "~/state/collectionJotai";
import type { CardType } from "~/types/card";
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH } from "~/utils/card";

export const CollectionCardGroupPreview = ({ card }: { card: CardType }) => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const fullCollectionlist = useAtomValue(fullCollectionlistJotai)!;
  // const total = collection.items.filter((c) => c.cardId === card.id).length;
  const { query: routerParams } = useRouter();
  const targetSet = routerParams.set as string | undefined;

  /**
   * collection management
   */
  const hasVariant = useMemo(() => {
    const targetCard = fullCollectionlist.find((c) => c.card.id === card.id);
    return targetCard?.groupings
      .filter(
        (g) =>
          !targetSet ||
          targetSet === "*" ||
          targetCard.card.variants.find((v) => v.id === g.variantId)?.setCard.set.code ===
            targetSet,
      )
      .find((grouping) => !!grouping.items.length);
  }, [fullCollectionlist, card.id, targetSet]);

  const hasEachVariant = useMemo(
    () =>
      card.variants.every(
        (v) =>
          fullCollectionlist
            .find((c) => c.card.id === card.id)
            ?.groupings.find((g) => g.variantId === v.id)?.items.length,
      ),
    [card.variants, card.id, fullCollectionlist],
  );

  const shading = useMemo(() => {
    if (hasEachVariant) {
      return "shadow-[0_0_14px_rgba(255,123,0,0.9)]";
    }
    if (hasVariant) {
      return "shadow-[0_0_14px_rgba(141,131,242,0.9)]";
    }
    return "";
  }, [hasEachVariant, hasVariant]);

  const variants = useMemo(
    () =>
      [...card.variants].reverse().map((v, i) => (
        <Image
          key={v.id}
          className={"absolute overflow-hidden rounded-[20px] shadow-[0_0_8px_rgba(0,0,0,0.5)]"}
          // constrain container size for library previews
          style={{
            top: 0 + i * 6,
          }}
          height={DEFAULT_CARD_HEIGHT}
          width={DEFAULT_CARD_WIDTH}
          alt={""}
          src={v.src}
        />
      )),
    [card.variants],
  );

  return (
    <>
      <div className={"flex flex-row items-center justify-center"}>
        <div
          className={`hover:!outline-primary relative cursor-pointer rounded-[20px] outline outline-1 
            outline-transparent transition-all hover:shadow-[0_0_14px_rgba(255,123,0,0.9)]
            ${shading}`}
        >
          <div
            className={"rounded-[20px] gradient-mask-b-40"}
            // constrain container size for library previews
            style={{
              height: DEFAULT_CARD_HEIGHT / 1.55,
              width: DEFAULT_CARD_WIDTH / 1.55,
            }}
          >
            {variants}

            <div
              className={"absolute left-0 top-0 h-full w-full "}
              style={{
                backgroundImage:
                  "linear-gradient(to top, rgb(2, 2, 2) 0%, rgba(2, 2, 2, 0.9) 25%, rgba(2, 2, 2, 0.6) 35%, transparent 55%)",
              }}
            />
          </div>
          <div className="absolute left-0 top-0 flex h-full w-full flex-col justify-end">
            <H4 className="mb-10 text-center !text-[1rem]">{card.variants.length} printings</H4>
          </div>
        </div>
      </div>

      {/* placeholder to preserve space */}
      <div className="flex h-[33px] cursor-pointer" />
    </>
  );
};
